import { z } from "zod";

import { getProfileIconUrl } from "@/shared/lib/champion-image";
import { get, post, request } from "@/shared/lib/http/zod-http";
import { api } from "@/shared/lib/api";

import {
  SearchUsersResponseSchema,
  GetUserTeamsResponseSchema,
  AddRiotAccountRequestSchema,
  AddRiotAccountResponseSchema,
  SetMainRiotAccountResponseSchema,
  UpdateUserProfileRequestSchema,
  UpdateUserProfileResponseSchema,
  userProfileDtoWithProNickSchema,
} from "./response-types.schemas";
import { parseRequestLoose } from "./parse-request-loose";
import {
  RiotAccountSchema,
  UserSearchResultSchema,
  UserTeamSchema,
} from "./user.schemas";
type UnknownRecord = Record<string, unknown>;

/**
 * Лёгкое summary пользователя (идентичность + бейджи). Для списков/подиума/hover —
 * вместо тяжёлого полного профиля /user/{id}, который раньше грузили веером.
 */
export const UserSummarySchema = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string().optional().default(""),
  avatar: z.string().nullable().optional(),
  banner: z.string().nullable().optional(),
  isVerified: z.boolean().optional().default(false),
  hasPro: z.boolean().optional().default(false),
  proNicknameStyle: z.number().optional().default(0),
  proNicknameColorA: z.string().nullable().optional(),
  proNicknameColorB: z.string().nullable().optional(),
  isOnline: z.boolean().optional().default(false),
  status: z.string().nullable().optional(),
  platformRoles: z.array(z.string()).optional().default([]),
  primaryPlatformRole: z.string().optional().default("User"),
});
export type UserSummary = z.infer<typeof UserSummarySchema>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null;

const readString = (
  record: UnknownRecord,
  keys: string[],
  fallback = "",
): string => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number") return String(value);
  }
  return fallback;
};

const readNumber = (
  record: UnknownRecord,
  keys: string[],
  fallback = 0,
): number => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return fallback;
};

export const userProfileDtoSchemaExtended = userProfileDtoWithProNickSchema;

export type UserProfile = z.infer<typeof userProfileDtoSchemaExtended> & {
  email?: string | null;
  isEmailVerified?: boolean;
  hasPassword?: boolean;
  hasPro?: boolean;
};

// Используем типы Team/RosterMember из shared/api/team

export type RiotAccount = z.infer<typeof RiotAccountSchema>;

export type UserSearchResult = z.infer<typeof UserSearchResultSchema>;

export type UserTeam = z.infer<typeof UserTeamSchema>;

export type UpdateUserRequest = z.infer<typeof UpdateUserProfileRequestSchema>;

const UsersListResponseSchema = z.object({
  users: z.array(UserSearchResultSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
  totalPages: z.number(),
});

// normalizeUserProfile удалена - нормализация выполняется автоматически через Axios interceptor

// Импортируем сгенерированные функции
import { getMe } from "@/shared/api/generated-kubb/hooks/useGetMe";
import { getUserId } from "@/shared/api/generated-kubb/hooks/useGetUserId";
import { getUserIdRiotAccounts } from "@/shared/api/generated-kubb/hooks/useGetUserIdRiotAccounts";
import { getUserIdTeams } from "@/shared/api/generated-kubb/hooks/useGetUserIdTeams";
import { getUserList } from "@/shared/api/generated-kubb/hooks/useGetUserList";
import { getUserUsernameUsername } from "@/shared/api/generated-kubb/hooks/useGetUserUsernameUsername";
import type { ResponseConfig } from "@/shared/lib/api";

// API functions
export const userApi = {
  // Короткая нормализация дат: бэк иногда отдаёт datetime без timezone (например "2025-12-17T10:05:24")
  // Zod .datetime() такое валит. Приводим к ISO с Z.
  _normalizeDateTimesInPlace: (root: unknown) => {
    const seen = new Set<object>();
    const isoNoTz =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?$/;
    const isoWithTz =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,7})?(?:Z|[+-]\d{2}:\d{2})$/;
    const spaceDateTime =
      /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d{1,7})?$/;

    const fix = (v: unknown) => {
      if (typeof v !== "string") return v;
      const s = v.trim();
      if (!s) return v;

      if (isoWithTz.test(s)) return s;
      if (isoNoTz.test(s)) return `${s}Z`;
      if (spaceDateTime.test(s)) return `${s.replace(" ", "T")}Z`;

      return v;
    };

    const walk = (x: unknown) => {
      if (!x || typeof x !== "object") return;
      const obj = x as object;
      if (seen.has(obj)) return;
      seen.add(obj);

      if (Array.isArray(x)) {
        for (let i = 0; i < x.length; i++) {
          const v = x[i];
          if (typeof v === "string") x[i] = fix(v);
          else walk(v);
        }
        return;
      }

      const record = x as UnknownRecord;
      for (const k of Object.keys(record)) {
        const v = record[k];
        if (typeof v === "string") record[k] = fix(v);
        else walk(v);
      }
    };

    walk(root);
  },

  _normalizeMatchHistoryKdaInPlace: (root: UnknownRecord) => {
    const arr = root["matchHistory"];
    if (!Array.isArray(arr)) return;
    const defaultKda = { kills: 0, deaths: 0, assists: 0 };
    for (let i = 0; i < arr.length; i++) {
      const item = arr[i];
      if (item && typeof item === "object" && (item as UnknownRecord)["kda"] === null) {
        (item as UnknownRecord)["kda"] = defaultKda;
      }
    }
  },

  _normalizeProNicknameStyleInPlace: (root: unknown) => {
    if (!root || typeof root !== "object" || Array.isArray(root)) return;
    const o = root as UnknownRecord;
    const v = o["proNicknameStyle"];
    if (typeof v !== "string") return;
    const key = v.trim().toLowerCase();
    const map: Record<string, number> = {
      none: 0,
      animatedgradient: 1,
      glow: 2,
      shine: 3,
    };
    if (key in map) {
      o["proNicknameStyle"] = map[key];
      return;
    }
    const n = Number(v);
    if (!Number.isNaN(n)) o["proNicknameStyle"] = n;
  },

  getProfile: async (): Promise<UserProfile> => {
    const response = (await getMe()) as unknown as ResponseConfig<unknown>;
    userApi._normalizeDateTimesInPlace(response.data);
    const data = response.data as UnknownRecord | null;
    if (data) userApi._normalizeMatchHistoryKdaInPlace(data);
    userApi._normalizeProNicknameStyleInPlace(response.data);
    const validated = userProfileDtoSchemaExtended.parse(response.data);
    return validated;
  },

  updateProfile: async (data: UpdateUserRequest): Promise<UserProfile> => {
    const validatedData = parseRequestLoose(UpdateUserProfileRequestSchema, data);
    const parsed = await post(
      "/me/update",
      validatedData,
      UpdateUserProfileResponseSchema,
    );
    return parsed.user;
  },

  getUserById: async (userId: string): Promise<UserProfile> => {
    const response = (await getUserId(
      userId,
    )) as unknown as ResponseConfig<unknown>;
    // Бэкенд возвращает данные в формате { user: profile }
    // response.data уже нормализован в Axios interceptor (PascalCase -> camelCase)
    const dataRecord = isRecord(response.data) ? response.data : null;
    const userData = dataRecord?.["user"] ?? response.data;
    if (!userData) {
      throw new Error("Пользователь не найден");
    }
    userApi._normalizeDateTimesInPlace(userData);
    if (isRecord(userData)) userApi._normalizeMatchHistoryKdaInPlace(userData);
    userApi._normalizeProNicknameStyleInPlace(userData);
    const validated = userProfileDtoSchemaExtended.parse(userData);
    return validated;
  },

  getUserByUsername: async (username: string): Promise<UserProfile> => {
    const safeUsername = String(username ?? "").trim();
    const encodedUsername = encodeURIComponent(safeUsername);
    const response = (await getUserUsernameUsername(
      encodedUsername,
    )) as unknown as ResponseConfig<unknown>;
    // Бэкенд возвращает данные в формате { user: profile }
    // response.data уже нормализован в Axios interceptor (PascalCase -> camelCase)
    const dataRecord = isRecord(response.data) ? response.data : null;
    const userData = dataRecord?.["user"] ?? response.data;
    if (!userData) {
      throw new Error("Пользователь не найден");
    }
    userApi._normalizeDateTimesInPlace(userData);
    if (isRecord(userData)) userApi._normalizeMatchHistoryKdaInPlace(userData);
    userApi._normalizeProNicknameStyleInPlace(userData);
    const validated = userProfileDtoSchemaExtended.parse(userData);
    return validated;
  },

  // Лёгкое summary одного пользователя (для hover-карточки/списков).
  getUserSummary: async (userId: string): Promise<UserSummary> => {
    const { data } = await api.get(`/user/${encodeURIComponent(userId)}/summary`);
    userApi._normalizeProNicknameStyleInPlace(data);
    return UserSummarySchema.parse(data);
  },

  // Батч-summary одним запросом (для списков участников и т.п.).
  getUserSummaries: async (userIds: string[]): Promise<UserSummary[]> => {
    const ids = Array.from(new Set((userIds ?? []).filter(Boolean)));
    if (ids.length === 0) return [];
    const { data } = await api.get(`/user/summaries`, {
      params: { ids: ids.join(",") },
    });
    const arr = Array.isArray(data) ? data : [];
    return arr.map((d) => {
      userApi._normalizeProNicknameStyleInPlace(d);
      return UserSummarySchema.parse(d);
    });
  },

  /** Обновить предпочитаемую игровую роль. null — сброс. */
  updatePreferredRole: async (
    preferredRole: string | null,
    secondaryRole: string | null,
  ): Promise<void> => {
    await api.patch("/me/preferred-role", { preferredRole, secondaryRole });
  },

  // Ленивая пагинируемая история матчей (вынесена из тяжёлого профиля).
  getUserMatches: async (
    userId: string,
    page = 1,
    pageSize = 20,
  ): Promise<NonNullable<UserProfile["matchHistory"]>> => {
    const { data } = await api.get(`/user/${encodeURIComponent(userId)}/matches`, {
      params: { page, pageSize },
    });
    const arr = Array.isArray(data) ? data : [];
    // Переиспользуем нормализатор kda (ожидает { matchHistory: [...] }).
    const wrapper = { matchHistory: arr } as UnknownRecord;
    userApi._normalizeMatchHistoryKdaInPlace(wrapper);
    return arr as NonNullable<UserProfile["matchHistory"]>;
  },

  // Search users
  searchUsers: async (query: string): Promise<UserSearchResult[]> => {
    const parsed = await get(
      `/user/search?q=${encodeURIComponent(query)}`,
      SearchUsersResponseSchema,
    );
    return parsed.users;
  },

  getAllUsers: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    status?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<{
    users: UserSearchResult[];
    total: number;
    page: number;
    size: number;
    totalPages: number;
  }> => {
    const response = (await getUserList({
      page: params?.page ?? 1,
      size: params?.size ?? 20,
      search: params?.search,
      status: params?.status,
      sortBy: params?.sortBy ?? "CreatedAt",
      sortOrder: params?.sortOrder ?? "desc",
    })) as unknown as ResponseConfig<unknown>;

    // swagger-тип здесь any, поэтому валидируем через zod
    const parsed = UsersListResponseSchema.parse(response.data);
    return {
      users: parsed.users,
      total: parsed.total,
      page: parsed.page,
      size: parsed.size,
      totalPages: parsed.totalPages,
    };
  },

  getUserTeams: async (userId: string): Promise<UserTeam[]> => {
    // Бэкенд возвращает массив напрямую, а не объект с полем teams
    const response = (await getUserIdTeams(
      userId,
    )) as unknown as ResponseConfig<unknown>;

    // Проверяем формат ответа
    if (Array.isArray(response.data)) {
      // Если это массив, валидируем каждый элемент
      // Бэкенд возвращает PascalCase поля (Id, Name, Tag, MemberCount)
      const teams = response.data.map((team: unknown) => {
        if (!isRecord(team)) {
          throw new Error("Некорректный формат команды");
        }
        // Преобразуем PascalCase в camelCase для валидации
        const normalizedTeam = {
          id: readString(team, ["Id", "id"]),
          name: readString(team, ["Name", "name"]),
          tag: readString(team, ["Tag", "tag"]),
          logoUrl: readString(team, ["LogoUrl", "logoUrl"]) || null,
          icon: readString(team, ["Icon", "icon"]),
          captainId: readString(team, ["CaptainId", "captainId"]),
          memberCount: readNumber(team, ["MemberCount", "memberCount"]),
          maxMembers: readNumber(team, ["MaxMembers", "maxMembers"], 5),
          createdAt: readString(team, ["CreatedAt", "createdAt"]),
          userRole: readString(team, ["UserRole", "userRole"]),
          isTemporary: Boolean(team["IsTemporary"] ?? team["isTemporary"] ?? false),
          tournamentId: (team["TournamentId"] ?? team["tournamentId"] ?? null) as string | null,
          archivedAt: (team["ArchivedAt"] ?? team["archivedAt"] ?? null) as string | null,
          canManageLft: Boolean(team["CanManageLft"] ?? team["canManageLft"] ?? false),
        };
        return UserTeamSchema.parse(normalizedTeam);
      });
      return teams;
    } else if (isRecord(response.data) && response.data["teams"]) {
      // Если это объект с полем teams, используем схему
      const parsed = GetUserTeamsResponseSchema.parse(response.data);
      return parsed.teams;
    } else {
      console.warn(
        "[userApi] getUserTeams: неожиданный формат ответа:",
        response.data,
      );
      return [];
    }
  },

  getUserRiotAccounts: async (userId: string): Promise<RiotAccount[]> => {
    const response = (await getUserIdRiotAccounts(
      userId,
    )) as unknown as ResponseConfig<unknown>;

    const raw = response.data;
    const list = Array.isArray(raw)
      ? raw
      : isRecord(raw) && Array.isArray(raw["accounts"])
        ? (raw["accounts"] as unknown[])
        : [];

    return list.map((item) => {
      if (!isRecord(item)) {
        throw new Error("Некорректный формат Riot аккаунта");
      }
      const profileIconId = readNumber(item, ["profileIconId", "ProfileIconId"]);
      const regionRaw = readString(item, ["region", "Region"], "EUW1");
      const regionParsed = RiotAccountSchema.shape.region.safeParse(regionRaw);
      const region = regionParsed.success ? regionParsed.data : "EUW1";

      return {
        id: readString(item, ["id", "Id"]),
        gameName: readString(item, ["gameName", "GameName"]),
        tagLine: readString(item, ["tagLine", "TagLine"]),
        region,
        verified: Boolean(
          item["verified"] ??
          item["Verified"] ??
          item["isVerified"] ??
          item["IsVerified"] ??
          false,
        ),
        profileIconId,
        avatar:
          profileIconId > 0 ? getProfileIconUrl(profileIconId) : "",
        isMain: Boolean(
          item["isMain"] ??
          item["IsMain"] ??
          item["isPrimary"] ??
          item["IsPrimary"] ??
          false,
        ),
        role:
          typeof item["role"] === "string"
            ? item["role"]
            : typeof item["Role"] === "string"
              ? item["Role"]
              : undefined,
        rank:
          typeof item["rank"] === "string"
            ? item["rank"]
            : typeof item["Rank"] === "string"
              ? item["Rank"]
              : typeof item["currentRank"] === "string"
                ? item["currentRank"]
                : typeof item["CurrentRank"] === "string"
                  ? item["CurrentRank"]
                  : undefined,
      };
    });
  },

  // Add riot account
  addRiotAccount: async (data: {
    gameName: string;
    tagLine: string;
    region: string;
  }): Promise<RiotAccount> => {
    const validatedData = parseRequestLoose(AddRiotAccountRequestSchema, data);
    const parsed = await post(
      "/user/riot-accounts",
      validatedData,
      AddRiotAccountResponseSchema,
    );
    return parsed.account;
  },

  removeRiotAccount: async (accountId: string): Promise<void> => {
    await request(
      { url: `/user/riot-accounts/${accountId}`, method: "delete" },
      z.void(),
    );
  },

  // Set main riot account
  setMainRiotAccount: async (accountId: string): Promise<RiotAccount> => {
    const parsed = await request(
      { url: `/user/riot-accounts/${accountId}/main`, method: "put" },
      SetMainRiotAccountResponseSchema,
    );
    return parsed.account;
  },
};
